"use client";

import { Card, StepLabel } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Step from "@mui/material/Step";
import Stepper from "@mui/material/Stepper";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import PageContainer from "@/components/atoms/PageContainer";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useTabsOrientation from "@/hooks/use-tabs-orientation";
import { getAccessToken } from "@/helpers/auth-helpers";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import { usePlatformId } from "@/hooks/requests/platform/use-platform-id";
import { PlatformsName } from "@/types/platform.types";
import useToast from "@/contexts/toast-context";
import useLoading from "@/hooks/use-loading";
import { WhatsAppBspConnectionMessages } from "@/messages/platforms/whatsappBspConnection/whatsapp-bsp-connection.messages";
import { BreadCrumb } from "@/types/page.types";
import customerPlatformService from "@/services/customer-platform.service";

import { Completion } from "./Completion";
import { Overview } from "./Overview";
import { OnboardingLink } from "./OnboardingLink";

type WhatsAppBspConnectionProps = {
  messages: WhatsAppBspConnectionMessages;
};

export function WhatsAppBspConnection({ messages }: WhatsAppBspConnectionProps) {
  const [appId, setAppId] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [appName, setAppName] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const router = useNavigate();
  const { lang } = useLanguage();
  const orientation = useTabsOrientation();
  const customerId = useGetCustomerId();
  const accessToken = getAccessToken();
  const { getPlatformByIdentifier, platforms } = usePlatformId();
  const { showToast } = useToast();
  const { isLoading, startLoading, endLoading } = useLoading();

  const platform =
    getPlatformByIdentifier("GUPSHUP" as PlatformsName) ||
    platforms?.find((p) => p.name?.toUpperCase() === "GUPSHUP" || p.id === 5);
  const platformId = platform?.id;

  const completeConnection = () => {
    router(ROUTE_URLS.workflow(lang));
  };

  const handleNext = () => {
    if (isLastStep()) return;
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleOnboardingComplete = useCallback(
    async (completedAppId: string, completedPhoneNumber: string, completedAppName: string) => {
      if (!platformId || !accessToken || !customerId) {
        showToast({
          duration: 10000,
          text: "Dados de configuração não disponíveis",
          type: "error",
        });
        return;
      }

      try {
        startLoading();

        setAppId(completedAppId);
        setPhoneNumber(completedPhoneNumber);
        setAppName(completedAppName);

        const callbackUrl = import.meta.env.VITE_GUPSHUP_CALLBACK_URL;

        if (!callbackUrl) {
          throw new Error("VITE_GUPSHUP_CALLBACK_URL não está configurada");
        }

        await customerPlatformService.gupshupConfigureSubscription(
          completedAppId,
          callbackUrl,
          {
            phoneNumber: completedPhoneNumber,
            appName: completedAppName,
          },
          platformId
        );

        setActiveStep((prevStep) => prevStep + 1);

        showToast({
          duration: 5000,
          text: "Subscription configurada com sucesso! Avançando para finalização...",
          type: "success",
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Erro ao configurar subscription";
        showToast({
          duration: 10000,
          text: errorMessage,
          type: "error",
        });
      } finally {
        endLoading();
      }
    },
    [platformId, accessToken, customerId, showToast, startLoading, endLoading]
  );

  const BREAD_CRUMBS: BreadCrumb[] = [
    { title: messages.breadCrumbTitle, href: ROUTE_URLS.platform(lang) },
  ];

  const steps = [
    {
      label: messages.overviewStepTitle,
      content: <Overview messages={messages} />,
    },
    {
      label: messages.onboardingLinkStepTitle,
      content: (
        <OnboardingLink messages={messages} onOnboardingComplete={handleOnboardingComplete} />
      ),
    },
    {
      label: messages.completionStepTitle,
      content: <Completion messages={messages} phoneNumber={phoneNumber || undefined} />,
    },
  ];

  const totalSteps = steps.length;

  const isLastStep = () => activeStep === totalSteps - 1;

  try {
    return (
      <PageLayout title={messages.pageTitle} messages={messages} breadCrumbs={BREAD_CRUMBS}>
        <PageContainer>
          <MDBox mt={5} mb={9}>
            <Grid container justifyContent="center">
              <Grid size={12}>
                <Card>
                  <MDBox mt={-3} mb={3} mx={2}>
                    <Stepper orientation={orientation} activeStep={activeStep} alternativeLabel>
                      {steps.map((step) => (
                        <Step key={step.label}>
                          <StepLabel>{step.label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </MDBox>
                  <MDBox p={2}>
                    <MDBox>
                      {steps[activeStep].content}
                      <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                        <MDBox />
                        {activeStep === 1 ? (
                          <MDBox />
                        ) : (
                          <MDButton
                            variant="gradient"
                            color="dark"
                            onClick={!isLastStep() ? handleNext : completeConnection}
                            disabled={isLoading}
                          >
                            {isLastStep() ? messages.completeButton : messages.nextButton}
                          </MDButton>
                        )}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        </PageContainer>
      </PageLayout>
    );
  } catch (error) {
    return (
      <div style={{ padding: "20px" }}>
        <h2>Erro ao renderizar componente WhatsApp BSP</h2>
        <p>{error instanceof Error ? error.message : "Erro desconhecido"}</p>
        <pre>{error instanceof Error ? error.stack : String(error)}</pre>
      </div>
    );
  }
}
